import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentClients.css";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  business_name: "",
  email: "",
  phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state_province: "",
  postal_code: "",
  country: "United States",
  existing_website_url: "",
  preferred_contact_method: "email",
  notes: "",
  active: true,
};

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function optionalText(value) {
  const cleaned = cleanText(value);

  return cleaned || null;
}

function normalizeClient(client = {}) {
  return {
    first_name:
      safeString(client.first_name),

    last_name:
      safeString(client.last_name),

    business_name:
      safeString(client.business_name),

    email:
      safeString(client.email),

    phone:
      safeString(client.phone),

    address_line_1:
      safeString(client.address_line_1),

    address_line_2:
      safeString(client.address_line_2),

    city:
      safeString(client.city),

    state_province:
      safeString(client.state_province),

    postal_code:
      safeString(client.postal_code),

    country:
      safeString(client.country) ||
      "United States",

    existing_website_url:
      safeString(
        client.existing_website_url
      ),

    preferred_contact_method:
      safeString(
        client.preferred_contact_method
      ) || "email",

    notes:
      safeString(client.notes),

    active:
      Number(client.active) === 1,
  };
}

function getClientName(client) {
  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (
    Number.isNaN(date.getTime())
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function DevelopmentClients() {
  const [clients, setClients] =
    useState([]);

  const [selectedClient, setSelectedClient] =
    useState(null);

  const [formData, setFormData] =
    useState({
      ...EMPTY_FORM,
    });

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("active");

  const [showForm, setShowForm] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadClients = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const response =
          await Api.getDevelopmentClients();

        setClients(
          response?.clients || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Clients could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filteredClients = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return clients.filter((client) => {
      const isActive =
        Number(client.active) === 1;

      if (
        statusFilter === "active" &&
        !isActive
      ) {
        return false;
      }

      if (
        statusFilter === "inactive" &&
        isActive
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchableText = [
        client.first_name,
        client.last_name,
        client.business_name,
        client.email,
        client.phone,
        client.city,
        client.state_province,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    clients,
    search,
    statusFilter,
  ]);

  const activeClientCount = useMemo(
    () =>
      clients.filter(
        (client) =>
          Number(client.active) === 1
      ).length,
    [clients]
  );

  const inactiveClientCount =
    clients.length - activeClientCount;

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewClientForm = () => {
    clearMessages();

    setSelectedClient(null);

    setFormData({
      ...EMPTY_FORM,
    });

    setShowForm(true);
  };

  const openEditClientForm = (
    client
  ) => {
    clearMessages();

    setSelectedClient(client);

    setFormData(
      normalizeClient(client)
    );

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setShowForm(false);

    setSelectedClient(null);

    setFormData({
      ...EMPTY_FORM,
    });
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const validateForm = () => {
    if (
      !cleanText(
        formData.first_name
      )
    ) {
      return "First name is required.";
    }

    if (
      !cleanText(
        formData.last_name
      )
    ) {
      return "Last name is required.";
    }

    if (
      !cleanText(
        formData.email
      )
    ) {
      return "Email is required.";
    }

    return "";
  };

  const buildPayload = () => ({
    first_name:
      cleanText(
        formData.first_name
      ),

    last_name:
      cleanText(
        formData.last_name
      ),

    business_name:
      optionalText(
        formData.business_name
      ),

    email:
      cleanText(
        formData.email
      ),

    phone:
      optionalText(
        formData.phone
      ),

    address_line_1:
      optionalText(
        formData.address_line_1
      ),

    address_line_2:
      optionalText(
        formData.address_line_2
      ),

    city:
      optionalText(
        formData.city
      ),

    state_province:
      optionalText(
        formData.state_province
      ),

    postal_code:
      optionalText(
        formData.postal_code
      ),

    country:
      cleanText(
        formData.country
      ) || "United States",

    existing_website_url:
      optionalText(
        formData.existing_website_url
      ),

    preferred_contact_method:
      cleanText(
        formData.preferred_contact_method
      ) || "email",

    notes:
      optionalText(
        formData.notes
      ),

    active:
      Boolean(formData.active),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedClient) {
        await Api.updateDevelopmentClient(
          selectedClient.id,
          payload
        );

        setSuccess(
          "Client updated successfully."
        );
      } else {
        await Api.createDevelopmentClient(
          payload
        );

        setSuccess(
          "Client created successfully."
        );
      }

      await loadClients();

      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The client could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const toggleClientStatus =
    async (client) => {
      clearMessages();

      const nextActive =
        Number(client.active) !== 1;

      const actionLabel =
        nextActive
          ? "reactivate"
          : "deactivate";

      const confirmed =
        window.confirm(
          `Are you sure you want to ${actionLabel} ${getClientName(
            client
          )}?`
        );

      if (!confirmed) {
        return;
      }

      try {
        await Api.updateDevelopmentClient(
          client.id,
          {
            active: nextActive,
          }
        );

        setSuccess(
          nextActive
            ? "Client reactivated."
            : "Client deactivated."
        );

        await loadClients();
      } catch (requestError) {
        setError(
          requestError.message ||
            `The client could not be ${actionLabel}d.`
        );
      }
    };

  return (
    <div className="development-clients-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Client management
          </p>

          <h2>Clients</h2>

          <p>
            Create and maintain client contact,
            business, address, and communication
            details.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewClientForm
            }
          >
            Add client
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <ClientForm
          formData={formData}
          selectedClient={
            selectedClient
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-client-summary-grid">
        <button
          type="button"
          className={`development-client-summary-card${
            statusFilter === "all"
              ? " active"
              : ""
          }`}
          onClick={() =>
            setStatusFilter("all")
          }
        >
          <span>Total clients</span>

          <strong>
            {clients.length}
          </strong>
        </button>

        <button
          type="button"
          className={`development-client-summary-card${
            statusFilter === "active"
              ? " active"
              : ""
          }`}
          onClick={() =>
            setStatusFilter("active")
          }
        >
          <span>Active</span>

          <strong>
            {activeClientCount}
          </strong>
        </button>

        <button
          type="button"
          className={`development-client-summary-card${
            statusFilter === "inactive"
              ? " active"
              : ""
          }`}
          onClick={() =>
            setStatusFilter(
              "inactive"
            )
          }
        >
          <span>Inactive</span>

          <strong>
            {inactiveClientCount}
          </strong>
        </button>
      </section>

      <section className="development-clients-panel">
        <div className="development-clients-toolbar">
          <div>
            <h3>Client records</h3>

            <p>
              {filteredClients.length} client
              {filteredClients.length === 1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-clients-filters">
            <label className="development-client-search">
              <span className="sr-only">
                Search clients
              </span>

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search clients..."
              />
            </label>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              aria-label="Filter clients by status"
            >
              <option value="all">
                All clients
              </option>

              <option value="active">
                Active clients
              </option>

              <option value="inactive">
                Inactive clients
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading clients...
          </div>
        ) : filteredClients.length ===
          0 ? (
          <div className="development-admin-empty">
            No clients match the current
            filters.
          </div>
        ) : (
          <div className="development-client-table-wrapper">
            <table className="development-client-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Preferred</th>
                  <th>Status</th>
                  <th>Updated</th>

                  <th>
                    <span className="sr-only">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredClients.map(
                  (client) => (
                    <tr key={client.id}>
                      <td>
                        <div className="development-client-primary-cell">
                          <strong>
                            {getClientName(
                              client
                            )}
                          </strong>

                          {client.business_name && (
                            <span>
                              {client.first_name}{" "}
                              {client.last_name}
                            </span>
                          )}

                          {client.existing_website_url && (
                            <a
                              href={
                                client.existing_website_url
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Existing website
                            </a>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="development-client-contact-cell">
                          <a
                            href={`mailto:${client.email}`}
                          >
                            {client.email}
                          </a>

                          <span>
                            {client.phone ||
                              "No phone"}
                          </span>
                        </div>
                      </td>

                      <td>
                        {[
                          client.city,
                          client.state_province,
                        ]
                          .filter(Boolean)
                          .join(", ") ||
                          "—"}
                      </td>

                      <td className="capitalize">
                        {client.preferred_contact_method ||
                          "email"}
                      </td>

                      <td>
                        <span
                          className={`development-client-status ${
                            Number(
                              client.active
                            ) === 1
                              ? "active"
                              : "inactive"
                          }`}
                        >
                          {Number(
                            client.active
                          ) === 1
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td>
                        {formatDate(
                          client.updated_at
                        )}
                      </td>

                      <td>
                        <ClientActions
                          client={client}
                          onEdit={() =>
                            openEditClientForm(
                              client
                            )
                          }
                          onToggleStatus={() =>
                            toggleClientStatus(
                              client
                            )
                          }
                        />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ClientForm({
  formData,
  selectedClient,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-client-form-panel">
      <div className="development-client-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedClient
              ? "Edit record"
              : "New record"}
          </p>

          <h3>
            {selectedClient
              ? `Edit ${getClientName(
                  selectedClient
                )}`
              : "Add client"}
          </h3>
        </div>

        <button
          type="button"
          className="development-client-close-button"
          onClick={onCancel}
          aria-label="Close client form"
        >
          ×
        </button>
      </div>

      <form
        className="development-client-form"
        onSubmit={onSubmit}
      >
        <div className="development-client-form-section">
          <h4>
            Primary information
          </h4>

          <div className="development-client-form-grid">
            <FormField
              label="First name"
              name="first_name"
              value={
                formData.first_name
              }
              onChange={onChange}
              required
            />

            <FormField
              label="Last name"
              name="last_name"
              value={
                formData.last_name
              }
              onChange={onChange}
              required
            />

            <FormField
              label="Business name"
              name="business_name"
              value={
                formData.business_name
              }
              onChange={onChange}
            />

            <FormField
              label="Email"
              name="email"
              type="email"
              value={
                formData.email
              }
              onChange={onChange}
              required
            />

            <FormField
              label="Phone"
              name="phone"
              type="tel"
              value={
                formData.phone
              }
              onChange={onChange}
            />

            <label className="development-client-field">
              <span>
                Preferred contact method
              </span>

              <select
                name="preferred_contact_method"
                value={
                  formData.preferred_contact_method ||
                  "email"
                }
                onChange={onChange}
              >
                <option value="email">
                  Email
                </option>

                <option value="phone">
                  Phone
                </option>

                <option value="text">
                  Text
                </option>
              </select>
            </label>

            <FormField
              label="Existing website"
              name="existing_website_url"
              type="url"
              value={
                formData.existing_website_url
              }
              onChange={onChange}
              placeholder="https://"
              fullWidth
            />
          </div>
        </div>

        <div className="development-client-form-section">
          <h4>Address</h4>

          <div className="development-client-form-grid">
            <FormField
              label="Address line 1"
              name="address_line_1"
              value={
                formData.address_line_1
              }
              onChange={onChange}
              fullWidth
            />

            <FormField
              label="Address line 2"
              name="address_line_2"
              value={
                formData.address_line_2
              }
              onChange={onChange}
              fullWidth
            />

            <FormField
              label="City"
              name="city"
              value={formData.city}
              onChange={onChange}
            />

            <FormField
              label="State or province"
              name="state_province"
              value={
                formData.state_province
              }
              onChange={onChange}
            />

            <FormField
              label="Postal code"
              name="postal_code"
              value={
                formData.postal_code
              }
              onChange={onChange}
            />

            <FormField
              label="Country"
              name="country"
              value={
                formData.country
              }
              onChange={onChange}
            />
          </div>
        </div>

        <div className="development-client-form-section">
          <h4>
            Internal information
          </h4>

          <label className="development-client-field full-width">
            <span>Notes</span>

            <textarea
              name="notes"
              value={
                formData.notes
              }
              onChange={onChange}
              rows="5"
              placeholder="Internal notes about the client..."
            />
          </label>

          <label className="development-client-checkbox">
            <input
              type="checkbox"
              name="active"
              checked={
                Boolean(
                  formData.active
                )
              }
              onChange={onChange}
            />

            <span>
              Client is active
            </span>
          </label>
        </div>

        <div className="development-client-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedClient
                ? "Save changes"
                : "Create client"}
          </button>
        </div>
      </form>
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  fullWidth = false,
}) {
  return (
    <label
      className={`development-client-field${
        fullWidth
          ? " full-width"
          : ""
      }`}
    >
      <span>
        {label}

        {required && (
          <span
            className="required-marker"
            aria-hidden="true"
          >
            *
          </span>
        )}
      </span>

      <input
        type={type}
        name={name}
        value={
          safeString(value)
        }
        onChange={onChange}
        required={required}
        placeholder={placeholder}
      />
    </label>
  );
}

function ClientActions({
  client,
  onEdit,
  onToggleStatus,
}) {
  const isActive =
    Number(client.active) === 1;

  return (
    <div className="development-client-actions">
      <button
        type="button"
        onClick={onEdit}
      >
        Edit
      </button>

      <Link
        to={`/avwakindevelopment/admin/projects?client_id=${client.id}`}
      >
        Projects
      </Link>

      <button
        type="button"
        className={
          isActive
            ? "danger"
            : "success"
        }
        onClick={onToggleStatus}
      >
        {isActive
          ? "Deactivate"
          : "Reactivate"}
      </button>
    </div>
  );
}

export default DevelopmentClients;